/**
 * Accessible modal dialog shell, used by DialogHost and any custom dialog.
 *
 * - role="dialog" + aria-modal, labelled by its title
 * - focus moves into the dialog, Tab is trapped, focus returns on close
 * - Esc and backdrop click call `onDismiss`
 */
import { useEffect, useId, useRef, type ReactNode } from 'react'
import './Dialog.css'

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface DialogProps {
  title: string
  children?: ReactNode
  footer?: ReactNode
  onDismiss: () => void
  /** Wider layout for content-heavy dialogs. */
  size?: 'sm' | 'md' | 'lg'
}

export function Dialog({ title, children, footer, onDismiss, size = 'sm' }: DialogProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    ;(focusables().find((el) => el.dataset['autofocus'] !== undefined) ?? focusables()[0] ?? panel)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onDismiss()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previouslyFocused?.focus?.()
    }
  }, [onDismiss])

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onDismiss()}>
      <div ref={panelRef} className={`dlg dlg--${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <h2 id={titleId} className="dlg-title">{title}</h2>
        {children && <div className="dlg-body">{children}</div>}
        {footer && <div className="dlg-footer">{footer}</div>}
      </div>
    </div>
  )
}
